import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property, query, queryAssignedNodes } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-dialog-css.js';

/**
 * `<zui-dialog>` is an overlay modal used to focus a user's attention.
 *
 * @element zui-dialog
 *
 * @slot - Default, unnamed slot; for inserting content into the body of `<zui-dialog>`
 * @slot header - Slot for title text
 * @slot footer - Slot for footer elements such as `<zui-button>` elements to close or confirm the dialog
 * @slot content - Deprecated: Use the default, unnamed slot instead
 *
 * @attr {boolean} [dialog-close=false] - Add this attribute to the corresponding footer element; when clicked it will cancel, close the dialog, and trigger the custom event 'close' with `event.detail=false`
 * @attr {boolean} [dialog-confirm=false] - Add this attribute to the corresponding footer element; when clicked it will confirm, close the dialog, and trigger the custom event 'close' with `event.detail=true`
 * @attr {boolean} [hide-backdrop=false] - Not recommended; this attribute removes the dialog backdrop color
 *
 * @csspart header - The header container inside `<zui-dialog>`; this is exposed as a CSS shadow part and can be accessed with `::part(header)`
 * @csspart content - The content container inside `<zui-dialog>`; this is exposed as a CSS shadow part and can be accessed with `::part(content)`
 * @csspart footer - The footer container inside `<zui-dialog>`; this is exposed as a CSS shadow part and can be accessed with `::part(footer)`
 *
 * @prop {boolean} [canceled=undefined | null] - Readonly; true if the dialog was canceled when it was last closed.
 *
 * @event close - Event dispatches once dialog is closed. If the dialog was closed by cancelling, `event.detail = false`. If the dialog was closed by confirming, `event.detail = true`.
 * @event open - Event dispatches once dialog is opened
 */
export class ZuiDialogElement extends ZuiBaseElement {
  #open = false;
  #canceled?: boolean | null | undefined;
  #contentObserver?: MutationObserver;
  static #instances = new Map<ZuiDialogElement, boolean>();

  // Used for checking if content is longer than dialog in order to overflow content and sticky the dialog footer
  #scrollbarVisible?: boolean = false;

  static get styles() {
    return [super.styles, style];
  }

  @query('dialog')
  private _dialogElement?: HTMLDialogElement;

  @queryAssignedNodes({ slot: 'header' })
  private _headerSlottedNodes: Array<HTMLCollection>;

  @queryAssignedNodes({ slot: 'footer' })
  private _footerSlottedNodes: Array<HTMLCollection>;

  /**
   * Represents if the dialog is currently open or not. Can apply to automatically open the dialog when attached to the DOM.
   */
  @property({ type: Boolean, reflect: true })
  get opened(): boolean {
    return this.#open;
  }

  set opened(val: boolean) {
    const oldVal = this.#open;
    if (oldVal !== val) {
      this.#open = val;
      this.requestUpdate('opened', oldVal);
      this.#ensureDialogState(val);
      val ? this.#dispatchOpenEvent() : this.#dispatchCloseEvent(false);
      val ? this.#isScrollbarVisible() : this.#resetScrollbarVisible();
      if (!val) {
        this.#canceled = true;
      }
    }
  }

  /**
   * When `true`, users cannot click outside the dialog card, in the translucent overlay, to close the dialog; default allows clicking overlay to close by cancel
   */
  @property({ type: Boolean, attribute: 'no-cancel-outside-dialog' })
  noCancelOutsideDialog = false;

  get canceled() {
    return this.#canceled;
  }

  /**
   * Opens a dialog
   */
  open(): void {
    this.opened = true;
  }

  /**
   *- Closes dialog, default argument is `false` meaning a close by cancel, pass in `true` for close by confirmation
   * @param {boolean} [confirm=false] - Close by confirmation or not
   */
  close(confirm = false): void {
    if (this.#open) {
      this.#open = false;
      this.#ensureDialogState(false);
      this.#canceled = !confirm;
      this.requestUpdate('opened');
      this.#dispatchCloseEvent(confirm);
      this.#resetScrollbarVisible();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-labelledby', 'dialogTitle');
    this.setAttribute('aria-describedby', 'dialogDesc');
    this.#setupContentMutationObserver();

    ZuiDialogElement.#instances.set(this, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.close(false);
    ZuiDialogElement.#instances.delete(this);
    this.#disconnectContentMutationObserver();
  }

  async firstUpdated() {
    this.#ensureDialogState(this.#open);

    this.#toggleHeader();
    this.#toggleFooter();
  }

  /**
   *
   * @param isOpen - Whether or not the dialog should be open
   * @param retries - Indicates how many attempts to retry ensuring the shadow dialog correctly represents the open state. Used to prevent infinitely calling itself.
   */
  #ensureDialogState(isOpen: boolean, retries = 1) {
    // disconnected elements cannot be in an "open" state
    if (!this.isConnected) {
      ZuiDialogElement.#instances.set(this, false);
      return;
    }

    const isDialogReady = this._dialogElement && this._dialogElement.isConnected;
    if (!isDialogReady && retries > 0) {
      this.requestUpdate();
      this.updateComplete.then(() => this.#ensureDialogState(isOpen, --retries));
      return;
    }

    // we should only call show/showModal/close if the dialog isn't currently in the target state; otherwise native code will throw an exception
    const shadowDialogOpen = this._dialogElement?.hasAttribute('open');
    if (isOpen && !shadowDialogOpen) {
      this._dialogElement.showModal();
    } else if (!isOpen && shadowDialogOpen) {
      this._dialogElement.close();
    }

    ZuiDialogElement.#instances.set(this, isOpen);
    const areAnyDialogsOpen = isOpen || Array.from(ZuiDialogElement.#instances.values()).find((x) => x);
    document.body.style.overflow = areAnyDialogsOpen ? 'hidden' : '';
  }

  #dispatchOpenEvent() {
    this.dispatchEvent(new CustomEvent('open', { bubbles: true, cancelable: true }));
  }

  #dispatchCloseEvent(wasDialogClosedByConfirm: boolean) {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, cancelable: true, detail: wasDialogClosedByConfirm }));
  }

  #footerActionHandler(e: Event) {
    const target = e.target as HTMLElement;
    if (target.hasAttribute('dialog-confirm')) {
      this.close(true);
    } else if (target.hasAttribute('dialog-close')) {
      this.close(false);
    }
  }

  #onSubmit(e: SubmitEvent) {
    // if the submit is of a form with the attribute `method="dialog"`, then close the dialog OR
    // if the submitter has the attribute `formmethod="dialog"`, then close the dialog
    if ((e.target as HTMLFormElement).method === 'dialog' || e.submitter?.getAttribute('formmethod') === 'dialog') {
      this.close();
    }
  }

  #wasBackdropClicked(event: Event) {
    if (event.target === this._dialogElement) {
      this.close(false);
    }
  }

  // Show or hide header slot based on whether or not it has content
  #toggleHeader() {
    const header: HTMLElement = this.shadowRoot.querySelector('.header');

    if (this._headerSlottedNodes.length === 0) {
      header.style.display = 'none';
    } else if (this._headerSlottedNodes.length > 0 && header.style.display === 'none') {
      header.style.removeProperty('display');
    }

    this.requestUpdate();
  }

  // Show or hide footer slot based on whether or not it has content
  #toggleFooter() {
    const footer: HTMLElement = this.shadowRoot.querySelector('.footer');

    if (this._footerSlottedNodes.length === 0) {
      footer.style.display = 'none';
    } else if (this._footerSlottedNodes.length > 0 && footer.style.display === 'none') {
      footer.style.removeProperty('display');
    }

    this.requestUpdate();
  }

  // Check if dialog scrollbar is visible
  async #isScrollbarVisible() {
    await this.updateComplete;

    if (!this.#scrollbarVisible && this._dialogElement?.scrollHeight > this._dialogElement?.clientHeight) {
      this.#scrollbarVisible = true;
    }

    this.requestUpdate();
  }

  // Reset this.#scrollbarVisible to false, usually when closing a dialog
  #resetScrollbarVisible() {
    if (this.#scrollbarVisible) {
      this.#scrollbarVisible = false;
    }

    this.requestUpdate();
  }

  // Setup MutationObserver to check if dialog content has changed
  #setupContentMutationObserver() {
    this.#contentObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList') {
          this.#isScrollbarVisible();
        }
      }
    });

    this.#contentObserver?.observe(this, { childList: true, subtree: true });

    this.requestUpdate();
  }

  // Disconnect MutationObserver
  #disconnectContentMutationObserver() {
    if (this.#contentObserver) {
      this.#contentObserver.disconnect();
    }
  }

  render() {
    return html`
      <dialog
        class="${classMap({ scrolling: this.#scrollbarVisible })}"
        @click=${(event: MouseEvent) => !this.noCancelOutsideDialog && this.#wasBackdropClicked(event)}
        @close="${() => this.close(false)}"
      >
        <article class="header-content-container">
          <header class="header" part="header" id="dialogTitle">
            <slot name="header" @slotchange="${this.#toggleHeader}"></slot>
          </header>
          <div class="content" part="content" id="dialogDesc">
            <slot @submit="${this.#onSubmit}"></slot>
            <slot name="content"></slot>
          </div>
        </article>
        <footer class="footer" part="footer">
          <slot name="footer" @click="${this.#footerActionHandler}" @slotchange="${this.#toggleFooter}"></slot>
        </footer>
      </dialog>
    `;
  }
}

window.customElements.define('zui-dialog', ZuiDialogElement);

export interface HTMLDialogElement extends HTMLElement {
  showModal?: VoidFunction;
  close?: VoidFunction;
  prototype: HTMLDialogElement;
}

declare global {
  interface HTMLElementTagNameMap {
    'zui-dialog': ZuiDialogElement;
  }

  interface Window {
    dialogPolyfill?: DialogPolyfill;
    HTMLDialogElement?: HTMLDialogElement;
    zywave?: {
      zui?: {
        dialogPolyfill?: DialogPolyfill;
      };
    };
  }
}

type DialogPolyfill = {
  registerDialog(dialog: HTMLElement): void;
};
